import { ApolloClient, NormalizedCacheObject } from '@apollo/client';
import {
  CreateTargetListDocument,
  CreateTargetListMutation,
  CreateTargetListMutationVariables,
  UpdateTargetListDocument,
  UpdateTargetListMutation,
  UpdateTargetListMutationVariables,
} from 'src/generated/client';
import { uploadTargetListToS3 } from './s3-upload';
import { createFormattedTargetList } from './parse-target-list';

export interface SegmentedUploadResult {
  success: boolean;
  createdLists: Array<{ id: number; name: string; npiCount: number }>;
  error?: string;
  totalNpiCount: number;
}

interface CreatedList {
  id: number;
  name: string;
  npiCount: number;
}

/**
 * Uploads multiple target lists from a segmented file.
 * Creates one list per segment with names: {baseName}__{segmentValue}
 * Implements all-or-nothing rollback: if any segment fails, archives all created lists.
 *
 * @param apolloClient - Apollo client for GraphQL mutations
 * @param baseName - Base name for target lists
 * @param description - Description for target lists
 * @param segments - Map of segment values to NPI arrays
 * @param getToken - Function to get auth token
 * @returns SegmentedUploadResult with success status and created lists
 */
export const uploadSegmentedTargetLists = async (
  apolloClient: ApolloClient<NormalizedCacheObject>,
  baseName: string,
  description: string,
  segments: Map<string, number[]>,
  getToken: () => Promise<string | null>,
): Promise<SegmentedUploadResult> => {
  const createdLists: CreatedList[] = [];
  let totalNpiCount = 0;

  try {
    // Process each segment sequentially to track failures
    for (const [segmentValue, npis] of segments.entries()) {
      const listName = createSegmentedListName(baseName, segmentValue);

      // Create the target list record
      const createResult = await apolloClient.mutate<CreateTargetListMutation, CreateTargetListMutationVariables>({
        mutation: CreateTargetListDocument,
        variables: {
          description: description,
          name: listName,
          uploadedFilename: `${listName}.txt`,
        },
      });

      const newTargetListId = createResult.data?.createTargetList?.targetList?.id;

      if (!newTargetListId) {
        throw new Error(`Failed to create target list for segment: ${segmentValue}`);
      }

      // Create formatted file with NPIs for this segment
      const formattedFile = await createFormattedTargetList(npis, newTargetListId);

      // Upload to S3
      const uploadResponse = await uploadTargetListToS3(formattedFile, newTargetListId, getToken);
      const npiCount = uploadResponse?.uploadedCount ?? npis.length;

      // Track created list
      createdLists.push({
        id: newTargetListId,
        name: listName,
        npiCount,
      });

      totalNpiCount += npiCount;
    }

    // All segments succeeded
    return {
      success: true,
      createdLists,
      totalNpiCount,
    };
  } catch (error) {
    // Rollback: Archive all created lists
    await rollbackCreatedLists(apolloClient, createdLists);

    return {
      success: false,
      createdLists: [],
      error: error instanceof Error ? error.message : 'Failed to upload segmented target lists',
      totalNpiCount: 0,
    };
  }
};

/**
 * Creates a segmented list name by combining base name with segment value.
 * Sanitizes segment value to be safe for list names.
 */
const createSegmentedListName = (baseName: string, segmentValue: string): string => {
  // Sanitize segment value: remove special characters, truncate length
  const sanitized = segmentValue
    .replace(/[^a-zA-Z0-9\-_\s]/g, '') // Remove special chars except dash, underscore, space
    .trim()
    .substring(0, 100); // Limit length

  return `${baseName}__${sanitized}`;
};

/**
 * Rolls back created lists by archiving them.
 * Uses archive instead of delete to preserve data and avoid foreign key issues.
 */
const rollbackCreatedLists = async (
  apolloClient: ApolloClient<NormalizedCacheObject>,
  createdLists: CreatedList[],
): Promise<void> => {
  // Archive all created lists
  const archivePromises = createdLists.map(async list => {
    try {
      await apolloClient.mutate<UpdateTargetListMutation, UpdateTargetListMutationVariables>({
        mutation: UpdateTargetListDocument,
        variables: {
          id: list.id,
          name: list.name,
          description: '[ROLLBACK] Failed segmented upload',
          isArchived: true,
        },
      });
    } catch (archiveError) {
      // Log error but don't throw - best effort cleanup
      console.error(`Failed to archive list ${list.name} (ID: ${list.id}) during rollback:`, archiveError);
    }
  });

  await Promise.all(archivePromises);
};
